/* eslint-disable react-hooks/exhaustive-deps */
import Loader from 'components/Loader';
import { Nav } from 'components/Nav';
import SideBar from 'components/SideBar';
import { HUB_ROLES } from 'constants/global';
import ROUTES from 'constants/routes';
import { signOut } from 'firebase/auth';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import useScreenSize from 'hooks/useScreenSize';
import useUser from 'hooks/useUser';
import { useEffect } from 'react';
import { Suspense, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate, Outlet, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setCurrentHub } from 'redux/slices/app';
import { auth, db, fetchCollectionByQueryLocal, getUser, modifyAuthError, setUser } from 'utils';
import capitalizeWords from 'utils/capitalizeWords';
import { formatDateValues } from 'utils/formatDateValues';
import { convertFromSlug, convertToSlug } from 'utils/slug';

const DashboardLayout = () => {
    const { currentHub } = useSelector((state) => state.app);
    const screenSize = useScreenSize();
    const mobileScreen = screenSize <= 640;
    const [sideBarOpen, setSideBarOpen] = useState(mobileScreen ? false : true);
    const [hubLoading, setHubLoading] = useState(true);
    const [resizedBelowMobile, setResizedBelowMobile] = useState(mobileScreen ? true : false);
    const { user } = useUser();
    const { hub } = useParams();
    const dispatch = useDispatch();
    const hubUser = user?.role === HUB_ROLES.AGENT || user?.role === HUB_ROLES.MANAGER;

    const logout = async () => {
        try {
            await signOut(auth);
            setUser(null);
        } catch (error) {
            toast.error(modifyAuthError(error?.code));
        }
    };

    useEffect(() => {
        if (getUser()?.id) {
            const unSubAutoUpdateRole = onSnapshot(doc(db, 'users', getUser().id), (doc) => {
                doc.exists() && setUser({ ...doc.data(), ...formatDateValues(doc.data()) });
            });

            return () => {
                unSubAutoUpdateRole();
            };
        }
    }, []);

    useEffect(() => {
        if (hubUser && hub) {
            fetchCollectionByQueryLocal(
                query(collection(db, 'hubs'), where('name', '==', convertFromSlug(hub))),
                setHubLoading,
                (hubs) => dispatch(setCurrentHub(hubs[0]))
            );
        }
    }, [hubUser, hub]);

    useEffect(() => {
        const handleResizeSidebar = () => {
            if (mobileScreen) {
                if (!sideBarOpen) {
                    setResizedBelowMobile(true);
                    return;
                }
                if (sideBarOpen && !resizedBelowMobile) {
                    setSideBarOpen(false);
                    setResizedBelowMobile(true);
                }
            } else {
                if (!sideBarOpen) {
                    setSideBarOpen(true);
                }
                setResizedBelowMobile(false);
            }
        };

        window.addEventListener('resize', handleResizeSidebar);
        return () => {
            window.removeEventListener('resize', handleResizeSidebar);
        };
    }, [mobileScreen, sideBarOpen, resizedBelowMobile]);

    if (!user) return <Navigate replace to={ROUTES.LOGIN} />;

    if (user && !user?.role) return <AccessPending name={capitalizeWords(user.fullName)} logout={logout} />;

    if ((user.role === HUB_ROLES.AGENT && !user?.hub) || (user.role === HUB_ROLES.MANAGER && !user?.hubs?.length)) {
        return <UnassignedHub name={capitalizeWords(user.fullName)} logout={logout} />;
    }

    if (hub === 'undefined' || hub === 'null') {
        return <Navigate replace to={`/hub/${convertToSlug(user?.hubs?.length ? user.hubs[0] : user?.hub)}/sales`} />;
    }

    if (hubUser) {
        if (hubLoading)
            return (
                <div className="h-screen w-screen flex items-center justify-center">
                    <Loader className="-mt-24 h-7 w-7" />
                </div>
            );

        // if hub not found, or you don't have access to the hub
        if (
            !currentHub ||
            (user.role === HUB_ROLES.AGENT
                ? !currentHub?.agents.find(({ value }) => value === user.id)
                : currentHub?.manager?.value !== user.id)
        ) {
            return <HubNotFound hubName={hub} logout={logout} />;
        }
    }

    return (
        <>
            <Nav setSideBarOpen={setSideBarOpen} sideBarOpen={sideBarOpen} />
            <div
                className={`grid h-[calc(100vh-58px)] overflow-y-hidden transition-all duration-300 ${
                    sideBarOpen ? 'grid-cols-[230px_1fr]' : 'grid-cols-[75px_1fr]'
                }`}
            >
                <SideBar sideBarOpen={sideBarOpen} setSideBarOpen={setSideBarOpen} mobileScreen={mobileScreen} />

                <main
                    className={`${
                        sideBarOpen && 'max-sm:col-span-2'
                    } h-full overflow-y-auto bg-[#FAFBFE] py-7 px-3 475:px-7`}
                    id="main-wrapper"
                >
                    <Suspense
                        fallback={
                            <div className="h-full w-full flex items-center justify-center">
                                <Loader />
                            </div>
                        }
                    >
                        <Outlet />

                        <footer className="text-right pt-4">
                            © Copyright Agro Market Square {new Date().getFullYear()} <br />
                            Built by{' '}
                            <a
                                href="https://kreatixtech.com/"
                                title="Kreatix Technologies"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Kreatix Technologies
                            </a>
                        </footer>
                    </Suspense>
                </main>
            </div>
        </>
    );
};

const AccessPending = ({ name, logout }) => {
    return (
        <div className="min-h-screen flex items-center justify-center bg-primary-bg px-4">
            <div className="max-w-md w-full bg-white max-400:px-4 p-8 pb-10 -translate-y-8 rounded-2xl shadow-modalShadow text-center">
                <h1 className="text-3xl font-bold text-primary mb-4">Pending Access</h1>
                <p className="text-text-light text-base mb-4">Awaiting role assignment from admin.</p>
                <p className="text-text text-base">
                    Please reach out to the admin for access permission for your account: <b>{name}</b>.
                </p>

                <button
                    className="flex items-center py-1.5 text-sm text-ams-red font-medium border-t px-5 border rounded-md mt-4 mx-auto border-ams-red/30"
                    onClick={logout}
                >
                    <span className="material-symbols-outlined text-lg mr-2">logout</span> Logout
                </button>
            </div>
        </div>
    );
};

const UnassignedHub = ({ name, logout }) => {
    return (
        <div className="min-h-screen flex items-center justify-center bg-primary-bg px-4">
            <div className="max-w-md w-full bg-white max-400:px-4 p-8 pb-10 -translate-y-8 rounded-2xl shadow-modalShadow text-center">
                <h1 className="text-3xl font-bold text-primary mb-4">Unassigned Retail Hub</h1>
                <p className="text-text-light text-base mb-4">You have not been assigned a retail hub.</p>
                <p className="text-text text-base">
                    Please reach out to the admin to assign a retail hub to your account with name: <b>{name}</b>.
                </p>

                <button
                    className="flex items-center py-1.5 text-sm text-ams-red font-medium border-t px-5 border rounded-md mt-4 mx-auto border-ams-red/30"
                    onClick={logout}
                >
                    <span className="material-symbols-outlined text-lg mr-2">logout</span> Logout
                </button>
            </div>
        </div>
    );
};

const HubNotFound = ({ hubName, logout }) => {
    return (
        <div className="min-h-screen flex items-center justify-center bg-primary-bg px-4">
            <div className="max-w-md w-full bg-white max-400:px-4 p-8 pb-10 -translate-y-8 rounded-2xl shadow-modalShadow text-center">
                <h1 className="text-2xl font-bold text-primary mb-4">Retail Hub Not Found</h1>
                <p className="text-text text-base">
                    {hubName !== 'undefined' && hubName !== 'null' ? (
                        <>
                            <b>{convertFromSlug(hubName)}</b> r
                        </>
                    ) : (
                        'R'
                    )}
                    etail hub not found
                </p>

                <Link
                    className="text-primary-2 mt-3 mb-1 flex items-center justify-center mx-auto"
                    to={ROUTES.HOMEPAGE}
                >
                    <span className="material-symbols-outlined text-lg mr-1">chevron_left</span>Go back
                </Link>

                <button
                    className="flex items-center py-1.5 text-sm text-ams-red font-medium border-t px-5 border rounded-md mt-4 mx-auto border-ams-red/30"
                    onClick={logout}
                >
                    <span className="material-symbols-outlined text-lg mr-2">logout</span> Logout
                </button>
            </div>
        </div>
    );
};

export default DashboardLayout;
